"use client";
import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { leadSubmit } from "@/helpers/PushLeadApi";
import { stateCityData } from "@/utils/stateCityData";
import Select from "react-select";

const SITE_KEY = process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY;
const COURSES_JSON_URL =
  "https://res.cloudinary.com/dhatss6sz/raw/upload/v1767614890/updated-inquiry-pop-up_gslo8b_jnsbow.json";

// Fallback brochure used when the selected specialization has no brochure of its own.
const DEFAULT_BROCHURE_URL = "https://cdn.msu.edu.in/msu-reavamp/pdf/MSU_Prospectus_2026.pdf";

const EMAIL_REGEX = /^[a-zA-Z0-9._%+-]{4,}@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,7}$/;
const MOBILE_REGEX = /^\d{10}$/;

const COUNTRY_CODES = [{ code: "+91", country: "India" }];

const INITIAL_FORM = {
  name: "",
  email: "",
  countryCode: "+91",
  mobile: "",
  programme: "",
  specialization: "",
  state: "",
  city: "",
  downloadBrochure: true,
};

// Custom react-select styles so the control height matches the other
// (px-20 py-14) text inputs in this form, instead of react-select's
// smaller default height.
const selectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: "52px",
    borderColor: state.isFocused ? "#005D82" : "#D7DFE6",
    boxShadow: "none",
    "&:hover": { borderColor: "#005D82" },
  }),
  valueContainer: (base) => ({
    ...base,
    padding: "2px 20px",
  }),
  placeholder: (base) => ({
    ...base,
    color: "#6b7280",
  }),
};

const BlogEnrollForm = ({ catId, defaultBrochureUrl = DEFAULT_BROCHURE_URL , slug=null }) => {
  const captchaRef = useRef(null);
  const widgetIdRef = useRef(null);

  const [courseRows, setCourseRows] = useState([]);
  const [formData, setFormData] = useState(INITIAL_FORM);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");

  const fetchProgrammes = useCallback(async () => {
    try {
      const res = await fetch(COURSES_JSON_URL);
      const data = await res.json();
      setCourseRows(data["Active Course"] || []);
    } catch (err) {
      console.error("Failed to load programme options:", err);
    }
  }, []);

  const formattedProgrammeOptions = useMemo(() => {
    const grouped = {};
    courseRows.forEach((item) => {
      if (!item.Program || !item.Specialization) return;
      if (!grouped[item.Program]) grouped[item.Program] = new Set();
      grouped[item.Program].add(item.Specialization);
    });
    return Object.entries(grouped).map(([program, specs]) => ({
      label: program,
      options: Array.from(specs).map((spec) => ({
        value: `${program}|||${spec}`,
        label: spec,
      })),
    }));
  }, [courseRows]);

  const brochureBySpecialization = useMemo(() => {
    const map = {};
    courseRows.forEach((item) => {
      if (!item.Program || !item.Specialization) return;
      const key = `${item.Program}|||${item.Specialization}`;
      if (item.Brochure) map[key] = item.Brochure;
    });
    return map;
  }, [courseRows]);

  const formattedStates = useMemo(
    () => Object.keys(stateCityData).map((s) => ({ value: s, label: s })),
    []
  );

  const formattedCities = useMemo(() => {
    const cityList = stateCityData[formData.state] || [];
    return cityList.map((c) => ({ value: c, label: c }));
  }, [formData.state]);

  useEffect(() => {
    let cancelled = false;

    function renderCaptcha() {
      if (!captchaRef.current || !window.grecaptcha) return;
      if (widgetIdRef.current !== null) return;

      captchaRef.current.innerHTML = "";
      widgetIdRef.current = window.grecaptcha.render(captchaRef.current, {
        sitekey: SITE_KEY,
        theme: "light",
        size: "normal",
      });
    }

    function tryRender() {
      if (window.grecaptcha && window.grecaptcha.render) {
        window.grecaptcha.ready(() => {
          if (!cancelled) renderCaptcha();
        });
      } else {
        const t = setInterval(() => {
          if (window.grecaptcha && window.grecaptcha.render) {
            clearInterval(t);
            if (!cancelled) {
              window.grecaptcha.ready(() => {
                if (!cancelled) renderCaptcha();
              });
            }
          }
        }, 100);
      }
    }

    tryRender();
    fetchProgrammes();

    return () => {
      cancelled = true;
      if (window.grecaptcha && widgetIdRef.current !== null) {
        window.grecaptcha.reset(widgetIdRef.current);
      }
    };
  }, [fetchProgrammes]);

  const clearMessages = () => {
    setSuccessMsg("");
    setErrors((prev) => ({ ...prev, submit: "", recaptcha: "" }));
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleSelectChange = (name, selectedOption) => {
    const value = selectedOption ? selectedOption.value : "";
    clearMessages();

    if (name === "specialization") {
      const [program, specialization] = value.split("|||");
      setFormData((prev) => ({ ...prev, programme: program || "", specialization: specialization || "" }));
      return;
    }

    if (name === "state") {
      setFormData((prev) => ({ ...prev, state: value, city: "" }));
      return;
    }

    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = "Name is required";
    if (!formData.email.trim()) newErrors.email = "Email is required";
    else if (!EMAIL_REGEX.test(formData.email)) newErrors.email = "Please enter a valid email";
    if (!formData.mobile.trim()) newErrors.mobile = "Mobile number is required";
    else if (!MOBILE_REGEX.test(formData.mobile)) newErrors.mobile = "Please enter a valid 10-digit mobile number";
    return newErrors;
  };

  // Downloads whichever brochure applies: the one tied to the chosen specialization,
  // or the default brochure if none is set / nothing was selected.
  // Fetches the PDF as a blob and saves it via an object URL so the browser
  // actually downloads the file instead of navigating away to it (which is
  // what a plain <a href> does for cross-origin PDFs).
  const triggerBrochureDownload = async () => {
    if (!formData.downloadBrochure) return;
    const key = `${formData.programme}|||${formData.specialization}`;
    const brochureUrl = brochureBySpecialization[key] || defaultBrochureUrl;
    if (!brochureUrl) return;

    const filename = brochureUrl.split("/").pop()?.split("?")[0] || "brochure.pdf";

    try {
      const res = await fetch(brochureUrl);
      if (!res.ok) throw new Error(`Failed to fetch brochure: ${res.status}`);
      const blob = await res.blob();
      const blobUrl = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
      // If the brochure host doesn't allow cross-origin fetches, fall back
      // to opening it in a new tab instead of failing silently.
      console.error("Brochure download failed, opening in a new tab instead:", err);
      window.open(brochureUrl, "_blank", "noopener,noreferrer");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    clearMessages();

    const newErrors = validateForm();
    if (Object.keys(newErrors).length) {
      setErrors(newErrors);
      return;
    }

    const token =
      typeof window !== "undefined" && window.grecaptcha && widgetIdRef.current !== null
        ? window.grecaptcha.getResponse(widgetIdRef.current)
        : "";

    if (!token) {
      setErrors((prev) => ({ ...prev, recaptcha: "Please verify you are not a robot." }));
      return;
    }

    try {
      setLoading(true);

      const verifyRes = await fetch("/api/verify-recaptcha", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token }),
      });
      const verifyJson = await verifyRes.json();
      if (!verifyRes.ok || !verifyJson.success) {
        throw new Error("reCAPTCHA verification failed");
      }

      const jsonData = {
        Name: formData.name,
        email: formData.email,
        phone: formData.countryCode + formData.mobile,
        // Programme: formData.programme,
        // Specialization: formData.specialization,
        // State: formData.state,
        // City: formData.city,
        BrochureDownload: formData.downloadBrochure,
        categories: [catId],
      };
      const payload = new FormData();
      payload.append("name", formData.name);
      payload.append("email", formData.email);
      payload.append("phone", formData.mobile);
      payload.append("programme", formData.programme);
      payload.append("Specialization", formData.specialization);
      payload.append("state", formData.state);
      payload.append("city", formData.city);
      payload.append("downloadBrochureStatus", formData.downloadBrochure); 
      payload.append("lead_from", `blog url:${process.env.NEXT_PUBLIC_CLIENT_URL}/blog/${slug}`)

      const response = await leadSubmit(jsonData, payload);

      if (response) {
        setSuccessMsg("Form submitted successfully!");
        await triggerBrochureDownload();
        setFormData(INITIAL_FORM);
        if (window.grecaptcha && widgetIdRef.current !== null) {
          window.grecaptcha.reset(widgetIdRef.current);
        }
      }
    } catch (err) {
      console.error("Submission failed:", err);
      setErrors((prev) => ({ ...prev, submit: "Submission failed. Please try again." }));
    } finally {
      setLoading(false);
    }
  };

  return (
<div className="border border-[#D7DFE6] px-[16px] py-[16px] lg:px-[44px] lg:py-[16px] mb-[60px] lg:mb-[10px]">
      <h2 className="text-xl lg:text-xl leading-[28px] lg:leading-[36px] mb-[12px] lg:mb-[16px] text-[#202124]">Explore Your Career Opportunities</h2>

      <form onSubmit={handleSubmit} className="flex flex-col gap-[12px] lg:gap-[16px]">
        {/* Name */}
          <div className="flex flex-col sm:flex-row gap-[12px]">

            <div className="flex flex-col">
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="Enter your name*"
                className={`w-full px-[16px] lg:px-[20px] py-[10px] lg:py-[12px] border ${errors.name ? "border-red-500" : "border-[#D7DFE6]"} outline-none focus:border-[#005D82]`}
              />
              {errors.name && <span className="text-red-500 text-[12px]">{errors.name}</span>}
            </div>
            {/* Email */}
            <div className="flex flex-col">
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="Enter your Email*"
                className={`w-full px-[16px] lg:px-[20px] py-[10px] lg:py-[12px] border ${errors.email ? "border-red-500" : "border-[#D7DFE6]"} outline-none focus:border-[#005D82]`}
              />
              {errors.email && <span className="text-red-500 text-[12px]">{errors.email}</span>}
            </div>
            
          </div>

        {/* Phone */}
        <div className="flex flex-col sm:flex-row gap-[12px]">
          <div className="relative w-full sm:w-[100px] shrink-0">
            <select
              name="countryCode"
              value={formData.countryCode}
              onChange={handleChange}
              className="appearance-none w-full pl-[16px] pr-[16px] py-[10px] lg:py-[12px] border border-[#D7DFE6] outline-none focus:border-[#005D82]"
            >
              {COUNTRY_CODES.map(({ code, country }) => (
                <option key={code} value={code} data-country={country}>
                  {code}
                </option>
              ))}
            </select>
          </div>
          <div className="flex-1 flex flex-col gap-[8px]">
            <input
              type="text"
              inputMode="numeric"
              pattern="\d*"
              name="mobile"
              value={formData.mobile}
              onChange={handleChange}
              placeholder="Enter your mobile no.*"
              maxLength={10}
              className={`w-full px-[16px] lg:px-[20px] py-[10px] lg:py-[12px] border ${errors.mobile ? "border-red-500" : "border-[#D7DFE6]"} outline-none focus:border-[#005D82]`}
            />
          </div>
        </div>
        {errors.mobile && <span className="text-red-500 text-[12px]">{errors.mobile}</span>}

        {/* Specialization (drives programme too) */}
        <Select
          name="specialization"
          aria-label="Select Specialization"
          className="w-full"
          styles={selectStyles}
          placeholder="Select Specialization"
          options={formattedProgrammeOptions}
          onChange={(opt) => handleSelectChange("specialization", opt)}
          maxMenuHeight={200}
          value={
            formData.specialization
              ? { value: `${formData.programme}|||${formData.specialization}`, label: formData.specialization }
              : null
          }
        />

        <div className="flex flex-col sm:flex-row gap-[12px]">
          <div className="flex-1 flex flex-col">
            {/* State */}
            <Select
              name="state"
              aria-label="Select State"
              placeholder="Select State"
              className="w-full"
              styles={selectStyles}
              options={formattedStates}
              onChange={(opt) => handleSelectChange("state", opt)}
              maxMenuHeight={200}
              value={formData.state ? formattedStates.find((s) => s.value === formData.state) : null}
            />
          </div>
          <div className="flex-1 flex flex-col">
            {/* City (depends on selected state) */}
            <Select
              name="city"
              aria-label="Select City"
              placeholder="Select City"
              className="w-full"
              styles={selectStyles}
              options={formattedCities}
              onChange={(opt) => handleSelectChange("city", opt)}
              maxMenuHeight={200}
              isDisabled={!formData.state}
              value={formData.city ? formattedCities.find((c) => c.value === formData.city) : null}
            />
          </div>
        </div>

        {/* Brochure */}
        <label className="flex items-center gap-[10px] lg:gap-[13px] cursor-pointer">
          <input
            type="checkbox"
            name="downloadBrochure"
            checked={formData.downloadBrochure}
            onChange={handleChange}
            className="w-[20px] h-[20px] lg:w-[22px] lg:h-[22px] shrink-0 accent-[#0475B3]"
            disabled
          />
          <span className="font-bold text-sm lg:text-base text-[#0475B3]">Download the programme brochure</span>
        </label>

        {/* reCAPTCHA explicit render target */}
        <div
            ref={captchaRef}
            className="h-[65px] overflow-hidden origin-top-left scale-90 [&>div]:max-w-full [&_iframe]:max-w-full"
          />

          {errors.recaptcha && (
            <span className="text-red-500 text-[12px]">{errors.recaptcha}</span>
          )}
        <button
          type="submit"
          disabled={loading}
          className="w-full bg-[#081E2D] hover:bg-[#004e6e] text-white py-[12px]"
        >
          {loading ? "Submitting..." : "SUBMIT"}
        </button>

        {errors.submit && <p className="text-red-500 mt-2">{errors.submit}</p>}
        {successMsg && <p className="text-green-500 mt-2">{successMsg}</p>}
      </form>
    </div>
  );
};

export default BlogEnrollForm;